/**
 * Turns the three inputs (`/chat/list-mine`'s result, `/chat-invitation/
 * list`'s result, and each query's loading/error state) into the exact
 * ordered rows the Messages route draws. Pure, no fetching, no hooks.
 */
import { formatUnreadCount, initialsFromTitle } from "./chatDisplay";
import { buildPreviewLine } from "./messagePreviewLine";
import { formatRelativeTime } from "./relativeTime";

export type typeChatListRow = {
  chatId: string;
  chatType: "pool" | "private" | "standalone";
  title: string;
  initials: string;
  /**
   * ISSUE-1355. The picture messaging-api resolved for this chat: the other
   * fan's photo for a private chat, the chat's own photo for a stand-alone
   * chat, null for a pool chat or on any miss. Null draws the initials plate.
   */
  avatarUrl: string | null;
  previewLine: string;
  timeLabel: string;
  unreadLabel: string | null;
  isMuted: boolean;
  isStrong: boolean;
  /** ISSUE-1362. True when the chat has unread messages, muted or not. */
  hasUnread: boolean;
};

export type typeInvitationRow = {
  chatInvitationId: string;
  title: string;
  initials: string;
  invitedByDisplayName: string;
  memberCount: number;
};

export type typeMessagesListState =
  | { phase: "loading" }
  | { phase: "failed" }
  | { phase: "empty" }
  | {
      phase: "ready";
      showRequestsRow: boolean;
      invitationRows: typeInvitationRow[];
      chatRows: typeChatListRow[];
      /** ISSUE-1357. The trimmed search text the rows were filtered by, or "". */
      searchText: string;
    };

/**
 * ISSUE-1357. Folds case and accents so "jose" finds "José" and "PICKS"
 * finds "Sunday picks". Pure, exported for its own test.
 */
export function normalizeChatSearchText(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

/**
 * ISSUE-1357. True when every word the fan typed appears somewhere in the
 * chat's title. A private chat's title is the other fan's name, so this
 * also finds a person, in either name order.
 */
export function chatTitleMatchesSearch(
  title: string,
  searchText: string,
): boolean {
  const query = normalizeChatSearchText(searchText);
  if (query === "") return true;
  const haystack = normalizeChatSearchText(title);
  return query
    .split(/\s+/)
    .every((word) => word === "" || haystack.includes(word));
}

export function resolveMessagesListState(input: {
  chats: {
    isLoading: boolean;
    hasFailed: boolean;
    /** ISSUE-1051 R2. A Try again the fan pressed is in flight. Wins over hasFailed. */
    isRetrying?: boolean;
    items:
      | readonly {
          chatId: string;
          chatType: "pool" | "private" | "standalone";
          title: string;
          lastMessagePreview: string | null;
          lastMessageSenderDisplayName?: string | null;
          lastMessageSenderIsSelf?: boolean;
          lastMessageAtIso: string;
          unreadCount: number;
          isMuted: boolean;
          isPendingApproval?: boolean;
          avatarUrl?: string | null;
        }[]
      | null;
    hasPendingRequestBadge: boolean;
  };
  invitations: {
    items:
      | readonly {
          chatInvitationId: string;
          title: string;
          invitedByDisplayName: string;
          memberCount: number;
        }[]
      | null;
  };
  nowMs: number;
  /**
   * ISSUE-1357. Filters the loaded chats and invitations by title on the
   * client, which is enough at launch: `/chat/list-mine` already returns
   * every chat the fan is in. The Requests row hides while searching, since
   * it is not a chat and never matches a name.
   */
  searchText?: string;
}): typeMessagesListState {
  if (input.chats.isRetrying === true) return { phase: "loading" };
  if (input.chats.hasFailed) return { phase: "failed" };
  if (input.chats.isLoading) return { phase: "loading" };

  const hasChats = input.chats.items !== null && input.chats.items.length > 0;
  const hasInvitations =
    input.invitations.items !== null && input.invitations.items.length > 0;

  // A PENDING MESSAGE REQUEST IS NOT AN EMPTY INBOX (ISSUE-831).
  //
  // This branch used to run before `showRequestsRow` was ever computed, so a
  // fan with requests and no chats got the empty state and no row to open,
  // while the bell and the tab badge both said something was waiting. That is
  // the FIRST TIME fan's path: somebody messages a new fan, and a new fan is
  // exactly the fan with zero chats.
  //
  // ISSUE-831 R1 offered two shapes: a ready state carrying only the requests
  // row, or an empty state that grows one. This is the first. The requests row
  // is a real row and the ready branch already draws it above the chats, so
  // the row lives in one place instead of two, and the empty state keeps its
  // one job of saying there is nothing.
  //
  // Fixed on both platforms in the same change, because
  // `apps/mobile/src/messages/messagesListState.ts` had the same defect for
  // the same reason and fixing one would leave the other telling the same lie.
  if (!hasChats && !hasInvitations && !input.chats.hasPendingRequestBadge) {
    return { phase: "empty" };
  }

  const searchText = (input.searchText ?? "").trim();

  const invitationRows: typeInvitationRow[] = (input.invitations.items ?? [])
    .filter((item) => chatTitleMatchesSearch(item.title, searchText))
    .map((item) => ({
      chatInvitationId: item.chatInvitationId,
      title: item.title,
      initials: initialsFromTitle(item.title),
      invitedByDisplayName: item.invitedByDisplayName,
      memberCount: item.memberCount,
    }));

  const chatRows: typeChatListRow[] = (input.chats.items ?? [])
    .filter((row) => row.isPendingApproval !== true)
    .filter((row) => chatTitleMatchesSearch(row.title, searchText))
    .map((row) => {
      const unreadLabel = row.isMuted
        ? null
        : row.unreadCount > 0
          ? formatUnreadCount(row.unreadCount)
          : null;
      return {
        chatId: row.chatId,
        chatType: row.chatType,
        title: row.title,
        initials: initialsFromTitle(row.title),
        avatarUrl: row.avatarUrl ?? null,
        previewLine: buildPreviewLine({
          preview: row.lastMessagePreview,
          senderDisplayName: row.lastMessageSenderDisplayName ?? null,
          senderIsSelf: row.lastMessageSenderIsSelf ?? false,
          emptyLabel: "No messages yet",
        }),
        timeLabel: formatRelativeTime(row.lastMessageAtIso, input.nowMs),
        unreadLabel,
        isMuted: row.isMuted,
        isStrong: unreadLabel !== null,
        hasUnread: row.unreadCount > 0,
      };
    });

  return {
    phase: "ready",
    showRequestsRow: input.chats.hasPendingRequestBadge && searchText === "",
    invitationRows,
    chatRows,
    searchText,
  };
}
